import { h } from 'preact';
import { SelectEntry, TextFieldEntry, TextAreaEntry } from '@epx/flow';

const getFireTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'timer', label: i18n['opt.Timer'] || 'Timer' },
  { value: 'signal', label: i18n['opt.Signal'] || 'Signal' },
  { value: 'completing', label: i18n['opt.completing'] || 'Completing' },
  { value: 'withdrawing', label: i18n['opt.withdrawing'] || 'Withdrawing' },
  { value: 'approving', label: i18n['opt.approving'] || 'Approving' },
  { value: 'rejecting', label: i18n['opt.rejecting'] || 'Rejecting' },
];

const getMethodTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'LocalService', label: i18n['opt.LocalService'] || 'Local Service' },
  { value: 'CSharpLibrary', label: i18n['opt.CSharpLibrary'] || 'C# Library' },
  { value: 'WebApi', label: i18n['opt.WebApi'] || 'WebAPI' },
  { value: 'StoreProcedure', label: i18n['opt.StoreProcedure'] || 'Stored Procedure' },
  { value: 'SQL', label: i18n['opt.SQL'] || 'SQL' },
  { value: 'Javascript', label: i18n['opt.Javascript'] || 'JavaScript' },
  { value: 'Python', label: i18n['opt.Python'] || 'Python' },
  { value: 'email', label: i18n['opt.email'] || 'Email' },
  { value: 'sms', label: i18n['opt.sms'] || 'SMS' },
];

const getSubMethodTypes = (i18n) => [
  { value: '', label: i18n['opt.none'] || '-- None --' },
  { value: 'HttpGet', label: i18n['opt.HttpGet'] || 'HTTP GET' },
  { value: 'HttpPost', label: i18n['opt.HttpPost'] || 'HTTP POST' },
  { value: 'HttpPut', label: i18n['opt.HttpPut'] || 'HTTP PUT' },
  { value: 'HttpDelete', label: i18n['opt.HttpDelete'] || 'HTTP DELETE' },
];

const TASK_TYPES = ['epx:UserTask', 'epx:ServiceTask', 'epx:ScriptTask'];

// Fields visible per methodType
const VISIBILITY = {
  LocalService: ['argus', 'expression'],
  CSharpLibrary: ['argus', 'assembly', 'typeFullName', 'methodName'],
  WebApi: ['subMethodType', 'argus', 'expression'],
  StoreProcedure: ['argus', 'expression'],
  SQL: ['argus', 'expression'],
  Javascript: ['argus', 'expression'],
  Python: ['argus', 'expression'],
  email: ['emailTo', 'emailCC', 'emailSubject', 'emailBody'],
  sms: [],
};

export class SfdActionPropertiesProvider {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n;
  }

  isApplicable(elementType) {
    return TASK_TYPES.includes(elementType);
  }

  getGroups(element) {
    const lf = this._lf;
    const i18n = this._i18n;
    const props = element.properties || {};
    const id = element.id;
    const actions = props.actions || [{}];
    const action = actions[0] || {};

    const update = (key, value) => {
      const updatedActions = [{ ...action, [key]: value }];
      lf.setProperties(id, { actions: updatedActions });
      lf.emit('node:properties-change', { id, properties: { actions: updatedActions } });
    };

    const methodType = action.methodType || '';
    const visibleFields = VISIBILITY[methodType] || [];
    const isVisible = (field) => visibleFields.includes(field);

    const entries = [
      {
        id: 'fireType',
        component: () => (
          <SelectEntry
            id="sfd-fireType"
            label={i18n.fireType || 'Fire Type'}
            value={action.fireType || ''}
            options={getFireTypes(i18n)}
            onChange={(val) => update('fireType', val)}
          />
        ),
      },
      {
        id: 'methodType',
        component: () => (
          <SelectEntry
            id="sfd-methodType"
            label={i18n.methodType || 'Method Type'}
            value={action.methodType || ''}
            options={getMethodTypes(i18n)}
            onChange={(val) => update('methodType', val)}
          />
        ),
      },
    ];

    if (isVisible('subMethodType')) {
      entries.push({
        id: 'subMethodType',
        component: () => (
          <SelectEntry
            id="sfd-subMethodType"
            label={i18n.subMethodType || 'Sub Method Type'}
            value={action.subMethodType || ''}
            options={getSubMethodTypes(i18n)}
            onChange={(val) => update('subMethodType', val)}
          />
        ),
      });
    }

    if (isVisible('argus')) {
      entries.push({
        id: 'argus',
        component: () => (
          <TextFieldEntry
            id="sfd-argus"
            label={i18n.argus || 'Arguments'}
            value={action.argus || ''}
            placeholder="key=value;key2=value2"
            debounce={true}
            onChange={(val) => update('argus', val)}
          />
        ),
      });
    }

    if (isVisible('expression')) {
      entries.push({
        id: 'expression',
        component: () => (
          <TextAreaEntry
            id="sfd-expression"
            label={i18n.expression || 'Expression'}
            value={action.expression || ''}
            placeholder="Enter expression..."
            debounce={true}
            onChange={(val) => update('expression', val)}
          />
        ),
      });
    }

    if (isVisible('typeFullName')) {
      entries.push({
        id: 'typeFullName',
        component: () => (
          <TextFieldEntry
            id="sfd-typeFullName"
            label={i18n.typeFullName || 'Type Full Name'}
            value={action.typeFullName || ''}
            onChange={(val) => update('typeFullName', val)}
          />
        ),
      });
    }

    if (isVisible('assembly')) {
      entries.push({
        id: 'assembly',
        component: () => (
          <TextFieldEntry
            id="sfd-assembly"
            label={i18n.assembly || 'Assembly'}
            value={action.assembly || ''}
            onChange={(val) => update('assembly', val)}
          />
        ),
      });
    }

    if (isVisible('methodName')) {
      entries.push({
        id: 'methodName',
        component: () => (
          <TextFieldEntry
            id="sfd-methodName"
            label={i18n.methodName || 'Method Name'}
            value={action.methodName || ''}
            onChange={(val) => update('methodName', val)}
          />
        ),
      });
    }

    if (isVisible('emailTo')) {
      entries.push(
        {
          id: 'emailTo',
          component: () => (
            <TextFieldEntry
              id="sfd-emailTo"
              label={i18n.emailTo || 'Email To'}
              value={action.emailTo || ''}
              onChange={(val) => update('emailTo', val)}
            />
          ),
        },
        {
          id: 'emailCC',
          component: () => (
            <TextFieldEntry
              id="sfd-emailCC"
              label={i18n.emailCC || 'Email CC'}
              value={action.emailCC || ''}
              onChange={(val) => update('emailCC', val)}
            />
          ),
        },
        {
          id: 'emailSubject',
          component: () => (
            <TextFieldEntry
              id="sfd-emailSubject"
              label={i18n.emailSubject || 'Email Subject'}
              value={action.emailSubject || ''}
              onChange={(val) => update('emailSubject', val)}
            />
          ),
        },
        {
          id: 'emailBody',
          component: () => (
            <TextAreaEntry
              id="sfd-emailBody"
              label={i18n.emailBody || 'Email Body'}
              value={action.emailBody || ''}
              onChange={(val) => update('emailBody', val)}
            />
          ),
        }
      );
    }

    return [
      {
        id: 'action',
        label: i18n.action || 'Action',
        entries,
        isEdited: () => !!(action.fireType || action.methodType),
      },
    ];
  }
}
